/**
 * True when the primary pointer is coarse — a finger rather than a mouse.
 *
 * Used to decide layout that depends on the DEVICE, not the viewport: a phone
 * cannot photograph its own screen, so a QR code shown there is only useful to
 * a second device, while a desktop with a webcam is the opposite. Viewport
 * width answers a different question (how much room is there) and gets this one
 * wrong in both directions — a narrow desktop window is still a desktop, and a
 * tablet in landscape is still a touch device.
 *
 * Falls back to `false` (assume desktop) when `matchMedia` is unavailable —
 * jsdom, SSR, very old browsers — because the desktop ordering keeps every
 * option on screen and merely ranks them differently. The touch ordering is the
 * one that tucks the QR behind a disclosure, so guessing "touch" wrongly costs
 * a click; guessing "desktop" wrongly costs nothing but ordering.
 */
import { useEffect, useState } from 'react';

const COARSE_POINTER_QUERY = '(pointer: coarse)';

function matchesCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(COARSE_POINTER_QUERY).matches;
  } catch {
    // A browser that doesn't understand the `pointer` feature throws or
    // reports no match; either way, treat it as a fine pointer.
    return false;
  }
}

export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(matchesCoarsePointer);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(COARSE_POINTER_QUERY);
    } catch {
      return;
    }

    // Re-read on mount: the initial state was seeded during render, which is
    // before hydration on any server-rendered path.
    setCoarse(mql.matches);

    const onChange = (event: MediaQueryListEvent) => setCoarse(event.matches);
    // Safari < 14 exposes only the deprecated addListener/removeListener pair.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return;
  }, []);

  return coarse;
}
